import { cloneDeep } from 'lodash';
import moment from 'moment';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { DateModel } from '../primitive/date.model';
import { TimeModel } from '../primitive/time.model';
import { CompositeModel } from './composite.model';

export enum TIMERECORDING_TYPE_OF_WORK_CODES {
    BREAK = 'BREK',
    OSR = 'OSR',
    WAIT = 'WAIT',
    PHONE_FIX = 'PFI',
    PHONE_FIX_EXPENSES = 'PFN',
    TRTO = 'TRTO',
    TRBK = 'TRBK',
    TRAU = 'TRAU',
    TRTU = 'TRTU',
    TRBU = 'TRBU',
    APPLICATION = 'APPL',
    APPLICATION_BILLABLE = 'APPX',
    OSR_BILLABLE = 'OSRX',
    APPLICATION_ECBD = 'ECAX',
    ECBD = 'ECBX',
    TRAVEL_TO_STANDBY = 'TRTS',
    TRAVEL_BACK_STANDBY = 'TRBS',
    SALES_SUPPORT = 'SSNW',
    ROSR = 'ROSR',
    ROSN = 'ROSN',
    FCC = 'FCC',
    FCCN = 'FCCN',
    OSR_AFTER_HOURS_CALL_OUT = '9213',
}

export enum TIMERECORDING_MEANS_OF_TRANSPORT_CODES {
    CAR = 'CAR',
    PASSENGER = 'PASS',
}

export enum TIMERECORDING_DESTINATION_CODES {
    HOME = 'TRHO',
    OFFICE = 'TROF',
}

export enum TIMERECORDING_ENTRY_VALIDITY {
    NO_TYPE_OF_WORK_SELECTED = 'timerecording-notypeofworkselected',
    OVERLAP_TIMERECORDING = 'timerecording-overlapdetected',
    OVERLAP_TIMESHEET = 'timerecording-overlaptimesheet',
    OVERLAP_OTHER_TIMERECORDING = 'timerecording-overlap-withothernotifications',
    START_OF_WORK_VIOLATED = 'timerecording-startofworkviolated',
    NEW_ENTRY_NO_TYPEOFWORK = 'timerecording-newentrynotypeofwork',
    NEW_ENTRY_NO_ACTIVITY = 'timerecording-newentrynoactivity',
    NEW_ENTRY_STARTS_BEFORE_TRTO = 'timerecording-startsbeforetrto',
    NEW_ENTRY_STARTS_EQUAL_TRTO = 'timerecording-startsequaltrto',
    NEW_ENTRY_STARTS_AFTER_TRBK = 'timerecording-startsaftertrbk',
    NEW_ENTRY_OSR_HAS_DURATION = 'timerecording-osrhasduration',
    NEW_ENTRY_END_BEFORE_START = 'timerecording-datetime-isbefore-starttime',
    TIMESHEET_SUBMITTED = 'timerecording-timesheet-submitted',
}

export abstract class TimeRecordingEntryModel extends CompositeModel {
    public startDateModel: DateModel;
    public startTimeModel: TimeModel;
    public endDateModel: DateModel;
    public endTimeModel: TimeModel;
    public id: number;
    public validity: TIMERECORDING_ENTRY_VALIDITY;
    public abstract getTypeOfWorkCode(): TIMERECORDING_TYPE_OF_WORK_CODES | undefined;

    public get startDateTime(): moment.Moment {
        return this.startDateModel.value;
    }

    public set startDateTime(newStartDateTime: moment.Moment) {
        // We already have an startDateTime and want to keep that reference. So we calculate the difference of the new value
        // to the existing value and just add this one.
        const difference = moment.duration(newStartDateTime.diff(this.startDateTime));
        const currentDateTime = this.startDateTime; // we store the moment object to keep the current reference
        currentDateTime.add(difference);

        this.startTimeModel = cloneDeep(this.startTimeModel); // make a clone deep to create reference, for triggering the onChanges hook
        this.startDateModel = cloneDeep(this.startDateModel);
        this.startDateModel.value = currentDateTime; // restore the old reference to stay in sync with startDateModel and startTimeModel
        this.startTimeModel.value = currentDateTime;
    }

    public get endDateTime(): moment.Moment {
        return this.endDateModel.value;
    }

    public set endDateTime(newEndDateTime: moment.Moment) {
        // We already have an endDateTime and want to keep that reference. So we calculate the difference of the new value
        // to the existing value and just add this one.
        const difference = moment.duration(newEndDateTime.diff(this.endDateTime));
        const currentDateTime = this.endDateTime; // we store the moment object to keep the current reference
        currentDateTime.add(difference);

        this.endDateModel = cloneDeep(this.endDateModel); // make a clone deep to create reference, for triggering the onChanges hook
        this.endTimeModel = cloneDeep(this.endTimeModel);
        this.endDateModel.value = currentDateTime; // restore the old reference to stay in sync with startDateModel and startTimeModel
        this.endTimeModel.value = currentDateTime;
    }

    public setStartDateTime(newStartDateTime: moment.Moment): void {
        this.startDateTime.set(newStartDateTime.clone().startOf('minute').toObject());
    }

    public setEndDateTime(newEndDateTime: moment.Moment): void {
        if (newEndDateTime.isSameOrAfter(this.startDateTime)) {
            this.endDateTime = newEndDateTime.clone().startOf('minute');
        }
    }

    public updateEndDateTime(): void {
        if (this.endTimeModel.value.isValid() && this.startDateTime.isAfter(this.endDateTime)) {
            this.endDateTime = this.startDateTime.clone().startOf('minute');
        }
    }

    public getFormattedDuration(): string {
        if (!this.endTimeModel.value.isValid()) {
            return '';
        }
        const duration = moment.duration(this.endDateTime.diff(this.startDateTime));
        if (duration.asMinutes() < 0) {
            return '';
        }
        return Math.floor(duration.asHours()) + 'h ' + duration.minutes() + 'm';
    }

    public getOutModel(): ODataTimeRecordingEntryOutModel {
        return <ODataTimeRecordingEntryOutModel>super.getOutModel();
    }

    public isWorkEntry(): boolean {
        switch (this.getTypeOfWorkCode()) {
            case TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION:
            case TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION_BILLABLE:
            case TIMERECORDING_TYPE_OF_WORK_CODES.OSR:
            case TIMERECORDING_TYPE_OF_WORK_CODES.OSR_BILLABLE:
            case TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION_ECBD:
            case TIMERECORDING_TYPE_OF_WORK_CODES.ECBD:
            case TIMERECORDING_TYPE_OF_WORK_CODES.BREAK:
            case TIMERECORDING_TYPE_OF_WORK_CODES.WAIT:
            case TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX:
            case TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES:
            case TIMERECORDING_TYPE_OF_WORK_CODES.SALES_SUPPORT:
            case TIMERECORDING_TYPE_OF_WORK_CODES.FCC:
            case TIMERECORDING_TYPE_OF_WORK_CODES.ROSR:
            case TIMERECORDING_TYPE_OF_WORK_CODES.FCCN:
            case TIMERECORDING_TYPE_OF_WORK_CODES.ROSN:
                // travel and travel related time recordings are not relevant work entries
                return true;
            default:
                return false;
        }
    }

    public isActivityRequired(): boolean {
        const workCode = this.getTypeOfWorkCode();
        return (
            workCode === TIMERECORDING_TYPE_OF_WORK_CODES.OSR ||
            workCode === TIMERECORDING_TYPE_OF_WORK_CODES.ROSR ||
            workCode === TIMERECORDING_TYPE_OF_WORK_CODES.ROSN ||
            workCode === TIMERECORDING_TYPE_OF_WORK_CODES.FCC ||
            workCode === TIMERECORDING_TYPE_OF_WORK_CODES.FCCN
        );
    }

    public isOverNightEntry(): boolean {
        return this.endDateModel.value.isValid() && !this.startDateModel.value.isSame(this.endDateModel.value, 'day');
    }

    public isTravelToEntry(): boolean {
        return (
            this.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO ||
            this.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY
        );
    }

    public isOSREntry(): boolean {
        return this.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.OSR;
    }
}

export interface ODataTimeRecordingEntryOutModel extends ODataObject {
    StartDay: string;
    StartTime: string;
    EndDay: string;
    EndTime: string;
    Billable: string;
    WorkCode: string;
}
